import React, { useState } from 'react';
import classNames from 'classnames';
import { Checkbox, Icon } from 'Components';
import Carousel from 'LearningManager/components/Carousel';
import CourseCard from 'LearningManager/components/CourseCard';
import AssignmentBadge from 'LearningManager/components/AssignmentBadge';
import Heading from 'LearningManager/components/Heading';
import DropdownMenu from '../Dropdown';
import {
	CourseData,
	LearningPathData
} from 'LearningManager/constants/UserDashboard.constants';
import { formatCardDate } from 'LearningManager/helpers/date';
import styles from './LearningPathCard.module.scss';

interface MenuOption {
	key: string;
	label: string;
	danger?: boolean;
}

interface Props {
	learningPath: LearningPathData;
	checked?: boolean;
	onCheck?: (id: string, checked: boolean) => void;
	menuOptions?: MenuOption[];
	menuDisabled?: boolean;
	onMenuAction?: (id: string, action: string) => void;
	onCourseClick?: (course: CourseData) => void;
}

const LearningPathCard: React.FC<Props> = ({
	learningPath,
	checked = false,
	onCheck,
	menuOptions,
	menuDisabled = false,
	onMenuAction,
	onCourseClick
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState<Set<number>>(new Set());

	const {
		id,
		title,
		statuses,
		courseCount,
		completedCount,
		completionPercentage,
		dueDate,
		startsDate,
		courses,
		learningPaths,
		individualCourses
	} = learningPath;

	const toggleSelection = (courseId: number) => {
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			next.has(courseId) ? next.delete(courseId) : next.add(courseId);
			return next;
		});
	};

	const renderCourseCard = (course: CourseData) => {
		const isSelected = selectedKeys.has(course.id);
		return (
			<CourseCard
				index={course.id}
				htmlId={`lms-lp-${id}-course-${course.id}`}
				course={{ ...course, isSelected }}
				onClick={() => onCourseClick?.(course)}
				onSelect={() => toggleSelection(course.id)}
				hideBadge
				hideRequiredSection
			/>
		);
	};

	const hasGroupedContent =
		(learningPaths && learningPaths.length > 0) ||
		(individualCourses && individualCourses.length > 0);

	const metaParts = [
		`${courseCount} ${courseCount === 1 ? 'course' : 'courses'}`,
		`${completedCount} of ${courseCount} complete`,
		...(dueDate ? [`Due ${formatCardDate(dueDate)}`] : []),
		...(startsDate ? [`Starts ${formatCardDate(startsDate)}`] : [])
	];

	return (
		<div className={styles.card}>
			<div className={styles.row} onClick={() => setIsOpen((o) => !o)}>
				{onCheck && (
					<div
						className={styles.checkbox}
						onClick={(e) => e.stopPropagation()}
					>
						<Checkbox
							checked={checked}
							onChange={(e) => onCheck(id, e.target.checked)}
						/>
					</div>
				)}

				<div className={styles.body}>
					<div className={styles.title}>{title}</div>

					<div className={styles.tags}>
						{statuses.map((s) => (
							<AssignmentBadge key={s} type={s} />
						))}
					</div>

					<div className={styles.progressBar}>
						<div
							className={styles.progressFill}
							style={{
								width: `${Math.min(completionPercentage, 100)}%`
							}}
						/>
					</div>

					<div className={styles.meta}>{metaParts.join(' | ')}</div>
				</div>

				<div
					className={classNames(styles.actions, {
						[styles['actions--withMenu']]: !!menuOptions
					})}
				>
					{menuOptions && (
						<div onClick={(e) => e.stopPropagation()}>
							<DropdownMenu
								arrow={false}
								disabled={menuDisabled}
								onChange={(key: string) =>
									onMenuAction?.(id, key)
								}
								options={menuOptions}
							>
								<div
									className={classNames(styles.moreBtn, {
										[styles[
											'moreBtn--disabled'
										]]: menuDisabled
									})}
								>
									<Icon type="optionIcon" role="img" />
								</div>
							</DropdownMenu>
						</div>
					)}

					<span
						className={classNames(styles.viewContent, {
							[styles['viewContent--open']]: isOpen
						})}
					>
						View content <Icon type="chevronDown" role="img" />
					</span>
				</div>
			</div>

			{isOpen && (
				<div className={styles.expanded}>
					{hasGroupedContent ? (
						<div className={styles.sections}>
							{learningPaths && learningPaths.length > 0 && (
								<div className={styles.container}>
									<Heading title="Learning Paths" />
									<div className={styles.section}>
										{learningPaths.map((group) => (
											<Carousel
												data={group.courses}
												label={group.label}
												labelClassName={
													styles.pathLabel
												}
												itemsPerPage={4}
												renderItem={renderCourseCard}
											/>
										))}
									</div>
								</div>
							)}

							{individualCourses && individualCourses.length > 0 && (
								<div className={styles.container}>
									<Heading title="Individual courses" />
									<Carousel
										data={individualCourses}
										itemsPerPage={4}
										renderItem={renderCourseCard}
									/>
								</div>
							)}
						</div>
					) : (
						<Carousel
							data={courses}
							itemsPerPage={4}
							renderItem={renderCourseCard}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default LearningPathCard;
